<template>
  <div class="row-md-12">
    <div class="row mt-5">
      <div class="col-md-12">
        <create-edit-location-form v-if="showCreateLocationForm"
          :edit-mode="editMode"
          :address-prop="address"
          :latitude-prop="String(latitude)"
          :longitude-prop="String(longitude)"
          :selected-location-type-prop="selectedLocationType"
          :require-presence-for-tool-return-prop="requirePresenceForToolReturn"
          :radius-prop="String(radius)"
          :editing-location-id="editingLocationId"
          @close="closeCreateLocationForm"
          @refresh-data="fetchData">
        </create-edit-location-form>
      </div>
      <div class="col-12">
        <card card-body-classes="table-full-width" :title="$t('locations.locations')" :on-add-object="openCreateLocationForm">
          <template v-if="enableExcelExport" #under-header>
            <div style="padding: 0 0 8px;">
              <el-button :icon="Download" size="small" @click="showExportDialog = true">{{ $t('dataExport.exportLocations') }}</el-button>
            </div>
          </template>
          <div>
            <div
              class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
            >
              <div class="col-12 col-md-4 col-sm-6 mb-2">
                <el-input
                  v-model="searchQuery"
                  class="search-input"
                  clearable
                  :prefix-icon="Search"
                  :placeholder="$t('common.searchRecords')"
                  aria-controls="datatables"
                  @keyup="search"
                  @clear="handleClearSearch"
                />
              </div>
            </div>

            <!-- Desktop Table -->
            <el-table v-if="!isMobile" :data="locations">
              <el-table-column
                v-for="column in tableColumns"
                :key="column.label"
                :min-width="column.minWidth"
                :prop="column.prop"
                :label="column.label"
              >
              </el-table-column>

              <el-table-column :label="$t('locations.toolCount')" min-width="80">
                <template #default="scope">
                  <span>{{ scope.row.toolCount }}</span>
                </template>
              </el-table-column>

              <el-table-column :label="$t('locations.coordinates')" min-width="150">
                <template #default="scope">
                  <div>
                    <div><strong>{{ $t('locations.latitude') }}:</strong> {{ scope.row.latitude }}</div>
                    <div><strong>{{ $t('locations.longitude') }}:</strong> {{ scope.row.longitude }}</div>
                    <div v-if="scope.row.radius !== null"><strong>{{ $t('locations.radius') }}:</strong> {{ scope.row.radius }}</div>

                  </div>
                </template>
              </el-table-column>

              <el-table-column :label="$t('locations.requirePresenceForToolReturn')" min-width="150">
                <template #default="scope">
                  <div>
                    {{ scope.row.requirePresenceForToolReturn ? $t('common.yes') : $t('common.no')}}
                  </div>
                </template>
              </el-table-column>

              <el-table-column :min-width="180" align="right" label="">
                <template #default="props">
                  <div>
                    <el-button
                      v-if="props.row.toolCount > 0"
                      type="primary"
                      circle
                      :title="$t('locations.moveTools')"
                      @click.stop="openMoveToolsModal(props.row)"
                    >
                      <el-icon>
                        <Rank />
                      </el-icon>
                    </el-button>

                    <el-button
                      type="warning"
                      circle
                      @click.stop="handleEdit(props.$index, props.row)"
                    >
                      <el-icon>
                        <Edit />
                      </el-icon>
                    </el-button>

                    <el-button
                      v-if="props.row.toolCount === 0"
                      type="danger"
                      circle
                      @click.stop="handleDelete(props.$index, props.row)"
                    >
                      <el-icon>
                        <Delete />
                      </el-icon>
                    </el-button>
                  </div>
                </template>
              </el-table-column>
            </el-table>


            <!-- Mobile Table -->
            <el-table v-else :data="locations">
              <el-table-column :label="$t('common.info')">
                <template #default="scope">
                  <div>
                    <div><strong>{{ $t('locations.address') }}:</strong> {{ scope.row.address }}</div>
                    <div><strong>{{ $t('common.type') }}:</strong> {{ scope.row.type }}</div>
                    <div><strong>{{ $t('locations.toolCount') }}:</strong> {{ scope.row.toolCount }}</div>
                    <div><strong>{{ $t('locations.requirePresenceForToolReturn') }}:</strong> {{ scope.row.requirePresenceForToolReturn ? $t('common.yes') : $t('common.no')}}</div>
                  </div>
                </template>
              </el-table-column>

              <el-table-column width="100" align="right" label="">
                <template #default="props">
                  <div class="d-flex justify-content-end">
                    <el-button
                      v-if="props.row.toolCount > 0"
                      type="primary"
                      circle
                      :title="$t('locations.moveTools')"
                      @click.stop="openMoveToolsModal(props.row)"
                    >
                      <el-icon>
                        <Rank />
                      </el-icon>
                    </el-button>

                    <el-button
                      type="warning"
                      circle
                      @click.stop="handleEdit(props.$index, props.row)"
                    >
                      <el-icon>
                        <Edit />
                      </el-icon>
                    </el-button>

                    <el-button
                      v-if="props.row.toolCount === 0"
                      type="danger"
                      circle
                      @click.stop="handleDelete(props.$index, props.row)"
                    >
                      <el-icon>
                        <Delete />
                      </el-icon>
                    </el-button>
                  </div>
                </template>
              </el-table-column>
            </el-table>


          </div>
          <template #footer>
            <div class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap pagination">
              <div class="">
                <p class="card-category">
                  {{ $t('pagination.showing') }} {{ from + 1 }} {{ $t('pagination.to') }} {{ to }} {{ $t('pagination.of') }} {{ total }} {{ $t('pagination.entries') }}
                </p>
              </div>
              <material-pagination color="info" class="pagination-no-border">
                <material-pagination-item
                  prev
                  :disabled="pagination.currentPage === 1"
                  @click="prevPage"
                />
                <material-pagination-item
                  v-for="page in displayedPages"
                  :key="page"
                  :label="String(page)"
                  :active="pagination.currentPage === page"
                  @click="changePage(page)"
                />
                <material-pagination-item
                  next
                  :disabled="pagination.currentPage === totalPages"
                  @click="nextPage"
                />
              </material-pagination>
            </div>
          </template>
        </card>
      </div>
    </div>

    <!-- Move Tools Modal -->
    <move-tools-modal
      v-model:show="showMoveToolsModal"
      :source-location-id="moveToolsSourceLocationId"
      :source-location-address="moveToolsSourceAddress"
      :locations="allLocations"
      @tools-moved="fetchData"
    />

    <data-export-modal v-model:show="showExportDialog" type="locations" />
  </div>
</template>
<script>
import api from "../../services/api"
import CreateEditLocationForm from "./CreateEditLocationForm";
import MoveToolsModal from "../../components/Modals/MoveToolsModal.vue";
import DataExportModal from "../../components/DataExportModal.vue";
import { ElTable, ElTableColumn, ElInput, ElButton, ElIcon } from 'element-plus';
import { Search, Rank, Download } from '@element-plus/icons-vue';
import MaterialPagination from '../../components/MaterialPagination.vue';
import MaterialPaginationItem from '../../components/MaterialPaginationItem.vue';
// import BaseButton from "../../components/BaseButton.vue";
import Card from "../../components/Card.vue";
import Cookies from 'js-cookie';
import swal from 'sweetalert2';
import { getCustomerConfig } from '../Settings/customerConfigHelper';

export default {
components: {
    CreateEditLocationForm,
    MoveToolsModal,
    DataExportModal,
    MaterialPagination,
    MaterialPaginationItem,
    ElTable,
    ElTableColumn,
    ElInput,
    ElButton,
    ElIcon,
    // BaseButton,
    Card
},
data() {
    return {
      Search,
      Rank,
      Download,
      showCreateLocationForm: false,
      showExportDialog: false,
      enableExcelExport: false,
      pagination: {
        perPage: 10,
        currentPage: 1,
        total: 0
      },
      tableColumns: [
        {
          prop: 'address',
          label: this.$t('locations.address'),
          minWidth: 150
        },
        {
          prop: 'type',
          label: this.$t('common.type'),
          minWidth: 90
        }
      ],
      searchQuery: '',
      locations: [],
      allLocations: [],
      isMobile: window.innerWidth <= 1024,

      editMode: false,
      selectedLocationType: '',
      longitude: '',
      latitude: '',
      address: '',
      radius: '',
      requirePresenceForToolReturn: false,
      editingLocationId: 0,

      showMoveToolsModal: false,
      moveToolsSourceLocationId: 0,
      moveToolsSourceAddress: ''
    };
},
computed: {
  to() {
    let highBound = this.from + this.pagination.perPage;
    if (this.total < highBound) {
      highBound = this.total;
    }
    return highBound;
  },
  from() {
    return this.pagination.perPage * (this.pagination.currentPage - 1);
  },
  total() {
    return this.pagination.total;
  },
  totalPages() {
    if (this.total > 0) {
      return Math.ceil(this.total / this.pagination.perPage);
    }
    return 1;
  },
  displayedPages() {
    const pages = [];
    const maxPages = Math.min(5, this.totalPages);

    let startPage = Math.max(1, this.pagination.currentPage - Math.floor(maxPages / 2));
    let endPage = startPage + maxPages - 1;

    if (endPage > this.totalPages) {
      endPage = this.totalPages;
      startPage = Math.max(1, endPage - maxPages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  }
},
  created() {

    // Check if the cookie exists
    const savedSearchQuery = Cookies.get('locationsSearchQuery');
    if (savedSearchQuery) {
      this.searchQuery = savedSearchQuery;
    } else {
      this.searchQuery = '';
    }

    const savedPage = Cookies.get('locationsPage');
    if (savedPage) {
      this.pagination.currentPage = Number(savedPage);
    } else {
      this.pagination.currentPage = 1
    }
    getCustomerConfig('DefaultPageSize')
    .then(config => {
      if(config === '')
      {
        this.pagination.perPage = 10
      }
      else
      {
        this.pagination.perPage = config
      }
      this.fetchData();
      this.fetchAllLocations();
    })
    getCustomerConfig('EnableExcelExport')
      .then(config => { this.enableExcelExport = config === '' ? false : config; });
    window.addEventListener('resize', this.updateIsMobile);
  },
beforeUnmount() {
    window.removeEventListener('resize', this.updateIsMobile);
},
methods: {
  updateIsMobile() {
    this.isMobile = window.innerWidth <= 1024;
  },
  search(event)
  {
    this.pagination.currentPage = 1;
    const queryLength = this.searchQuery.length;
    if (event.key === 'Backspace' || event.key === 'Enter') {
      this.fetchData();
    }
    else {
      if (queryLength > 1) {
        if (queryLength % 2 === 0) {
          this.fetchData();
        }
      }
    }
  },
  handleClearSearch()
  {
    Cookies.remove('locationsPage');
    this.pagination.currentPage = 1
    this.fetchData();
  },
  updatePage(item){
    this.pagination.currentPage = item;
    Cookies.set('locationsPage', this.pagination.currentPage, { expires: new Date(new Date().getTime() + 1 * 60 * 1000) });
    this.fetchData()
  },
  changePage(page) {
    if (page !== this.pagination.currentPage && page >= 1 && page <= this.totalPages) {
      this.pagination.currentPage = page;
      Cookies.set('locationsPage', this.pagination.currentPage, { expires: new Date(new Date().getTime() + 1 * 60 * 1000) });
      this.fetchData();
    }
  },
  nextPage() {
    if (this.pagination.currentPage < this.totalPages) {
      this.changePage(this.pagination.currentPage + 1);
    }
  },
  prevPage() {
    if (this.pagination.currentPage > 1) {
      this.changePage(this.pagination.currentPage - 1);
    }
  },
  openCreateLocationForm()
  {
    this.showCreateLocationForm = true;
    this.$nextTick(() => {
      window.scrollTo(0, 0);
    });
  },
  closeCreateLocationForm()
  {
      this.showCreateLocationForm = false;
      this.address = '';
      this.longitude = '';
      this.latitude = '';
      this.selectedLocationType = '';
      this.requirePresenceForToolReturn = false;
      this.editingLocationId = 0
      this.radius = '';
      this.editMode = false;
  },
  deleteLocation(row)
  {
    let deleteUrl = `/Location/` + row.id
    api.delete(deleteUrl)
    .then(() => {
      this.locations = this.locations.filter(loc => loc.id !== row.id);
      this.fetchAllLocations();
      swal.fire({
        title: this.$t('delete.deleted'),
        text: `${this.$t('delete.youDeleted')} ${row.address}`,
        icon: 'success',
        customClass: {
          confirmButton: 'btn btn-success btn-fill',
        },
        buttonsStyling: false
      });
    })
    .catch(error => {
      console.error('API request error:', error); // eslint-disable-line no-console
      const message = error.response?.data?.detail || error.response?.data?.title || this.$t('locations.cannotDeleteHasTools');
      swal.fire({
        title: this.$t('common.somethingWentWrong'),
        text: message,
        icon: 'error',
        customClass: {
          confirmButton: 'btn btn-danger btn-fill',
        },
        buttonsStyling: false
      });
    });
  },
  fetchAllLocations() {
    api.get('/Location?currentPage=1&pageSize=1000')
    .then(response => {
      this.allLocations = response.data.result.map(item => ({
        id: item.id,
        address: item.address
      }));
    })
    .catch(error => {
      console.error('API request error:', error); // eslint-disable-line no-console
    });
  },
  fetchData() {
      let getLocationsUrl = ''
      if(this.searchQuery === '')
      {
          Cookies.remove('locationsSearchQuery');
          getLocationsUrl = `/Location?currentPage=${this.pagination.currentPage}&pageSize=${this.pagination.perPage}&includeToolInfo=true`
      }
      else
      {
         //Store searchQuery as cookie - 5 minute expiry time
        Cookies.set('locationsSearchQuery', this.searchQuery, { expires: new Date(new Date().getTime() + 1 * 60 * 1000) });
        getLocationsUrl = `/Location?currentPage=${this.pagination.currentPage}&pageSize=${this.pagination.perPage}&address=${this.searchQuery}&includeToolInfo=true`
      }
      api.get(getLocationsUrl)
      .then(response => {
        this.pagination.total = response.data.totalItems;
        this.locations = response.data.result.map(item => {
          return {
            id: item.id,
            address: item.address,
            type: item.locationType.type,
            latitude: item.latitude,
            longitude: item.longitude,
            radius: item.radiusMeters,
            requirePresenceForToolReturn: item.requirePresenceForToolReturn,
            toolCount: item.toolsBelongingToLocation ?? 0
          };
        });
      })
      .catch(error => {
        console.error('API request error:', error); // eslint-disable-line no-console
        this.$toast.error(this.$t('common.unableToRetrieveData'), { timeout: 0 });
      });
  },
  openMoveToolsModal(row) {
    this.moveToolsSourceLocationId = row.id;
    this.moveToolsSourceAddress = row.address;
    this.showMoveToolsModal = true;
  },
    handleDelete(index, row) {
      swal.fire({
        title: this.$t('delete.areYouSure'),
        text: this.$t('locations.areYouSureDelete'),
        icon: 'warning',
        showCancelButton: true,
        customClass: {
          confirmButton: 'btn btn-success btn-fill',
          cancelButton: 'btn btn-danger btn-fill'
        },
        confirmButtonText: this.$t('delete.yesDeleteIt'),
        cancelButtonText: this.$t('delete.cancel'),
        buttonsStyling: false
      }).then(result => {
        if (result.value) {
          this.deleteLocation(row);
        }
      });
    },
    handleEdit(index, row)
    {
      this.editMode = true;
      this.address = row.address;
      this.longitude = row.longitude;
      this.latitude = row.latitude;
      this.selectedLocationType = row.type;
      this.editingLocationId = row.id;
      this.requirePresenceForToolReturn = row.requirePresenceForToolReturn;
      this.radius = row.radius
      if(this.latitude === null){
        this.latitude = ''
      }
      if(this.longitude === null){
        this.longitude = ''
      }
      if(this.radius === null){
        this.radius = ''
      }
      this.openCreateLocationForm()
    },
  },
};
</script>
<style></style>
